import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Add, Edit, PersonOff, PersonAddAlt1 } from '@mui/icons-material';
import {
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumn } from 'mui-datatables';
import { useSnackbar } from 'notistack';

import { closeDialog } from '@/components/Dialog';
import openConfirmDialog from '@/components/openConfirmDialog';
import { useAuth, useAuthorization } from '@/hooks/auth';
import AppError from '@/interfaces/AppError';
import { ISystemUser } from '@/interfaces/system-users/ISystemUser';
import { UserRoles } from '@/interfaces/system-users/UserRoles';
import systemUsersService from '@/services/system-users';

function EditButton({ systemUserId }: { systemUserId: string }) {
  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    navigate(`/system-users/${systemUserId}`);
  }, [navigate, systemUserId]);
  const { checkUserHasRole } = useAuthorization();

  return (
    <IconButton
      onClick={handleClick}
      disabled={!checkUserHasRole([UserRoles.ADMIN])}
    >
      <Edit />
    </IconButton>
  );
}

function ActivationButton({
  systemUser,
  onChanged,
}: {
  systemUser: Pick<ISystemUser, 'id' | 'name' | 'active'>;
  onChanged: () => void;
}) {
  const { checkUserHasRole } = useAuthorization();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const isSelf = user?.id === systemUser.id;
  const isActive = systemUser.active !== false;
  const disabled = !checkUserHasRole([UserRoles.ADMIN]) || isSelf;

  const handleClick = useCallback(() => {
    openConfirmDialog({
      title: isActive ? 'Deactivate user?' : 'Reactivate user?',
      content: isActive
        ? `${systemUser.name} will no longer be able to sign in and will be hidden from this list. Their subscriptions and sales history are kept.`
        : `${systemUser.name} will be able to sign in again.`,
      confirmButtonText: isActive ? 'Deactivate' : 'Reactivate',
      onConfirm: async () => {
        try {
          if (isActive) {
            await systemUsersService.deactivateSystemUser(systemUser.id);
          } else {
            await systemUsersService.reactivateSystemUser(systemUser.id);
          }
          enqueueSnackbar(
            isActive ? 'User deactivated' : 'User reactivated',
            { variant: 'success' }
          );
          closeDialog();
          onChanged();
        } catch (error) {
          console.error(error);
          enqueueSnackbar(
            error instanceof AppError
              ? error.message
              : 'Unable to update the user. Please, try again later',
            { variant: 'error' }
          );
        }
      },
    });
  }, [enqueueSnackbar, isActive, onChanged, systemUser.id, systemUser.name]);

  const button = (
    <span>
      <IconButton onClick={handleClick} disabled={disabled}>
        {isActive ? <PersonOff /> : <PersonAddAlt1 />}
      </IconButton>
    </span>
  );

  if (isSelf) {
    return <Tooltip title="You cannot deactivate your own account">{button}</Tooltip>;
  }

  return (
    <Tooltip title={isActive ? 'Deactivate user' : 'Reactivate user'}>
      {button}
    </Tooltip>
  );
}

export default function ListSystemUsers() {
  const navigate = useNavigate();
  const { checkUserHasRole } = useAuthorization();

  const [systemUsersList, setSystemUsersList] = useState<ISystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchSystemUsers = useCallback(async () => {
    setLoading(true);
    try {
      const systemUsers = await systemUsersService.listSystemUsers({
        includeInactive: showInactive,
      });
      setSystemUsersList(systemUsers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchSystemUsers();
  }, [fetchSystemUsers]);

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: 'id',
        label: 'ID',
        options: {
          filter: false,
          sort: false,
          display: false,
          print: false,
          download: false,
          viewColumns: false,
        },
      },
      {
        name: 'name',
        label: 'Name',
        options: {
          filter: true,
          filterType: 'textField',
          sort: true,
        },
      },
      {
        name: 'email',
        label: 'Email',
        options: {
          filter: true,
          filterType: 'textField',
          sort: true,
        },
      },
      {
        name: 'roles',
        label: 'Roles',
        options: {
          filter: true,
          filterType: 'multiselect',
          sort: true,
          customBodyRender: value => value.join(', '),
        },
      },
      {
        name: 'active',
        label: 'Status',
        options: {
          filter: true,
          sort: true,
          display: showInactive,
          customBodyRender: value =>
            value === false ? (
              <Chip label="Inactive" size="small" />
            ) : (
              <Chip label="Active" size="small" color="success" />
            ),
        },
      },
      {
        name: 'Actions',
        options: {
          filter: false,
          sort: false,
          print: false,
          download: false,
          customBodyRender: (value, tableMeta) => {
            const rowUser = systemUsersList.find(
              systemUser => systemUser.id === tableMeta.rowData[0]
            );
            if (!rowUser) {
              return null;
            }
            return (
              <Stack direction="row">
                <EditButton systemUserId={rowUser.id} />
                <ActivationButton
                  systemUser={rowUser}
                  onChanged={fetchSystemUsers}
                />
              </Stack>
            );
          },
        },
      },
    ],
    [fetchSystemUsers, showInactive, systemUsersList]
  );

  const toolbar = useCallback(
    () => (
      <>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={event => setShowInactive(event.target.checked)}
            />
          }
          label="Show inactive"
        />
        <Button
          onClick={() => {
            navigate('/system-users/new');
          }}
          variant="outlined"
          disabled={!checkUserHasRole([UserRoles.ADMIN])}
          startIcon={<Add />}
        >
          New user
        </Button>
      </>
    ),
    [checkUserHasRole, navigate, showInactive]
  );

  return (
    <MUIDataTable
      title="System Users"
      data={systemUsersList}
      columns={columns}
      options={{
        rowHover: true,
        selectableRows: 'none',
        customToolbar: toolbar,
        textLabels: {
          body: {
            noMatch: loading ? (
              <CircularProgress />
            ) : (
              'Sorry, no matching records found'
            ),
          },
        },
      }}
    />
  );
}
